import React, { useState, useEffect, useCallback } from "react";
// Changed from "../api/axios" to use the specialized Admin instance[cite: 14]
import adminAxios from "./adminAxios"; 

const colors = {
  primary: "#03f8d7",
  accent: "#ff2d75",
  deepBg: "#060608",
  surface: "rgba(13, 13, 18, 0.95)",
  textMain: "#ffffff",
  textMuted: "#64748b",
  border: "rgba(3, 248, 215, 0.15)",
  success: "#00c853",
  error: "#ff1744",
  overlay: "rgba(0, 0, 0, 0.9)"
};

const FinanceManagement = () => {
  const [activeTab, setActiveTab] = useState('wallet');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [notif, setNotif] = useState({ show: false, msg: '', type: 'success' });

  // Retrieve isolated tenant context identifier
  const tenantId = localStorage.getItem("adminId") || "global";

  // List Management
  const [blockchains, setBlockchains] = useState(['TRC20', 'BEP20', 'ERC20', 'BTC', 'SOL', 'USDT']);
  const [apps, setApps] = useState(['PayPal', 'Venmo', 'Google Pay', 'Paytm', 'Phonepe', 'UPI', 'Alipay']);
  
  // Custom Modals State
  const [showAddModal, setShowAddModal] = useState(null); 
  const [showConfirmDelete, setShowConfirmDelete] = useState(null); 
  const [newName, setNewName] = useState('');

  const [cryptoData, setCryptoData] = useState({ blockchain: 'TRC20', address: '' });
  const [thirdPartyData, setThirdPartyData] = useState({ appName: 'PayPal', details: '', receiverName: '' });
  const [bankData, setBankData] = useState({ bankName: '', receiverName: '', accountNumber: '', ifsc: '' });

  const triggerNotif = (msg, type = 'success') => {
    setNotif({ show: true, msg, type });
    setTimeout(() => setNotif({ show: false, msg: '', type: 'success' }), 3000);
  };

  const fetchSettings = useCallback(async () => {
    try {
      // adminAxios automatically handles baseURL/api and handshake headers[cite: 14]
      const res = await adminAxios.get("finance/settings", { params: { tenantId } });
      if (res.data) setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to sync finance settings:", err);
      setHistory([]);
    }
  }, [tenantId]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const resetForms = () => {
    setCryptoData({ blockchain: blockchains[0], address: '' });
    setThirdPartyData({ appName: apps[0], details: '', receiverName: '' });
    setBankData({ bankName: '', receiverName: '', accountNumber: '', ifsc: '' });
    setEditingId(null);
  };

  const handleConfirmAdd = () => {
    if (!newName.trim()) return;
    if (showAddModal === 'Blockchain') {
      setBlockchains([...blockchains, newName.trim()]);
      setCryptoData({ ...cryptoData, blockchain: newName.trim() });
    } else {
      setApps([...apps, newName.trim()]);
      setThirdPartyData({ ...thirdPartyData, appName: newName.trim() });
    }
    setNewName('');
    setShowAddModal(null);
    triggerNotif(`${newName.toUpperCase()} ADDED`);
  };

  const removeListItem = (type, name) => {
    if (type === 'Blockchain') {
      setBlockchains(blockchains.filter(b => b !== name));
      if (cryptoData.blockchain === name) setCryptoData({...cryptoData, blockchain: blockchains[0]});
    } else {
      setApps(apps.filter(a => a !== name));
      if (thirdPartyData.appName === name) setThirdPartyData({...thirdPartyData, appName: apps[0]});
    }
    triggerNotif("ITEM REMOVED FROM LIST", "error");
  };

  /**
   * THE FIX: Updated to "finance/admin/update-wallet" to match backend routes[cite: 14]
   */
  const handleUpdateWallet = async () => {
    try {
      setLoading(true);
      const payload = {
        blockchain: cryptoData.blockchain,
        address: cryptoData.address,
        tenantId
      };

      if (editingId) {
        payload.id = String(editingId).trim();
      }

      await adminAxios.post("finance/admin/update-wallet", payload);
      triggerNotif(editingId ? "WALLET UPDATED" : "WALLET ADDED TO LEDGER");
      
      resetForms();
      fetchSettings(); 
    } catch (err) {
      triggerNotif("SYNC FAILED", "error");
    } finally { setLoading(false); }
  };

  /**
   * THE FIX: Updated to "finance/admin/update-bank-app" to match backend routes[cite: 14]
   */
  const handleUpdateBankApp = (type) => async () => {
    try {
      setLoading(true);
      const targetId = editingId ? String(editingId).trim() : "";
      await adminAxios.post("finance/admin/update-bank-app", { 
        id: targetId, 
        bankData, 
        thirdPartyData, 
        type,
        tenantId
      });
      triggerNotif(`${type.toUpperCase()} SYNCED`);
      resetForms();
      fetchSettings();
    } catch (err) {
      triggerNotif("SYNC FAILED", "error");
    } finally { setLoading(false); }
  };

  const handleEdit = (item) => {
    const cleanId = item._id ? String(item._id) : null;
    setEditingId(cleanId);
    if (item.type === 'crypto') {
      setActiveTab('wallet');
      setCryptoData({ blockchain: item.crypto.blockchain, address: item.crypto.address });
    } else if (item.type === 'bank') {
      setActiveTab('bank');
      setBankData({ ...item.bank });
    } else if (item.type === 'thirdParty') {
      setActiveTab('thirdparty');
      setThirdPartyData({ ...item.thirdParty });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * THE FIX: Updated to "finance/admin/delete/" to match backend routes[cite: 14]
   */
  const handleDeleteEntry = async () => {
    if (!showConfirmDelete) return;
    try {
      setLoading(true);
      const targetId = String(showConfirmDelete).trim();
      await adminAxios.delete(`finance/admin/delete/${targetId}`, { data: { tenantId } });
      triggerNotif("ENTRY REMOVED", "error");
      setShowConfirmDelete(null);
      fetchSettings();
    } catch (err) {
      triggerNotif("DELETE FAILED", "error");
    } finally { setLoading(false); }
  };

  // --- Styles ---
  const containerStyle = { padding: "20px", color: colors.textMain, fontFamily: "'Inter', sans-serif", minHeight: "100vh" };
  const tabButtonStyle = (isActive) => ({
    padding: "12px 24px", cursor: "pointer", background: isActive ? "rgba(3, 248, 215, 0.1)" : "transparent",
    color: isActive ? colors.primary : colors.textMuted, border: `1px solid ${isActive ? colors.primary : colors.border}`,
    borderRadius: "8px", marginRight: "10px", fontWeight: "bold", fontSize: "11px", letterSpacing: "1px"
  });
  const cardStyle = { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: "16px", padding: "24px", marginTop: "20px", maxWidth: "600px" };
  const inputStyle = { width: "100%", padding: "14px", marginTop: "8px", marginBottom: "16px", background: "#111", border: `1px solid ${colors.border}`, borderRadius: "8px", color: "#fff", boxSizing: "border-box" };
  const btnBase = { width: "100%", padding: "16px", border: "none", borderRadius: "8px", fontWeight: "900", cursor: "pointer", marginTop: "10px" };

  const filteredHistory = history.filter(item => {
    if (activeTab === 'wallet') return item.type === 'crypto';
    if (activeTab === 'bank') return item.type === 'bank';
    if (activeTab === 'thirdparty') return item.type === 'thirdParty';
    return false;
  });

  return (
    <div style={containerStyle} data-tenant-scope={tenantId}>
      {/* Notifications and Modals */}
      {notif.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', padding: '15px 25px', background: notif.type === 'success' ? colors.success : colors.error, color: '#fff', borderRadius: '8px', fontWeight: 'bold', zIndex: 3000, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
          {notif.msg}
        </div>
      )}

      {showConfirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: colors.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2500 }}>
          <div style={{ background: '#111', padding: '30px', borderRadius: '16px', border: `1px solid ${colors.accent}`, width: '350px', textAlign: 'center' }}>
            <h4 style={{ color: colors.accent, marginBottom: '10px' }}>CONFIRM REMOVAL</h4>
            <p style={{ color: colors.textMuted, fontSize: '13px', marginBottom: '20px' }}>This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button disabled={loading} onClick={handleDeleteEntry} style={{ flex: 1, padding: '12px', background: colors.accent, border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', color: '#fff' }}>DELETE</button>
              <button disabled={loading} onClick={() => setShowConfirmDelete(null)} style={{ flex: 1, padding: '12px', background: 'transparent', border: `1px solid ${colors.border}`, color: '#fff', borderRadius: '6px', cursor: 'pointer' }}>CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: colors.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#111', padding: '30px', borderRadius: '16px', border: `1px solid ${colors.primary}`, width: '350px' }}>
            <h4 style={{ margin: '0 0 20px 0', color: colors.primary }}>ADD NEW {showAddModal.toUpperCase()}</h4>
            <input autoFocus style={inputStyle} placeholder="Enter name..." value={newName} onChange={(e) => setNewName(e.target.value)} />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={handleConfirmAdd} style={{ flex: 1, padding: '12px', background: colors.primary, border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>ADD</button>
              <button onClick={() => setShowAddModal(null)} style={{ flex: 1, padding: '12px', background: 'transparent', border: `1px solid ${colors.border}`, color: '#fff', borderRadius: '6px', cursor: 'pointer' }}>CANCEL</button>
            </div>
          </div>
        </div>
      )}

      <h2 style={{ letterSpacing: "2px", marginBottom: "30px", fontWeight: "900" }}>FINANCE MANAGEMENT</h2>
      
      <div style={{ display: "flex", marginBottom: "10px", flexWrap: "wrap", gap: "10px" }}>
        <button style={tabButtonStyle(activeTab === 'wallet')} onClick={() => {setActiveTab('wallet'); resetForms();}}>WALLET ADDRESS</button>
        <button style={tabButtonStyle(activeTab === 'bank')} onClick={() => {setActiveTab('bank'); resetForms();}}>BANK DETAILS</button>
        <button style={tabButtonStyle(activeTab === 'thirdparty')} onClick={() => {setActiveTab('thirdparty'); resetForms();}}>THIRD PARTY APPS</button>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: colors.primary, fontSize: "14px", margin: 0 }}>{activeTab.toUpperCase()} CONFIGURATION</h3>
            {(activeTab === 'wallet' || activeTab === 'thirdparty') && (
                <button onClick={() => setShowAddModal(activeTab === 'wallet' ? 'Blockchain' : 'App')} style={{ background: 'transparent', border: `1px solid ${colors.primary}`, color: colors.primary, borderRadius: '4px', fontSize: '10px', padding: '4px 8px', cursor: 'pointer' }}>+ ADD TO LIST</button>
            )}
        </div>

        {activeTab === 'wallet' && (
          <>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <select style={inputStyle} value={cryptoData.blockchain} onChange={(e) => setCryptoData({...cryptoData, blockchain: e.target.value})}>
                    {blockchains.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                <button onClick={() => removeListItem('Blockchain', cryptoData.blockchain)} style={{ background: 'none', border: 'none', color: colors.accent, cursor: 'pointer', marginBottom: '10px' }}>🗑️</button>
            </div>
            <input style={inputStyle} placeholder="Paste Wallet Address" value={cryptoData.address} onChange={(e) => setCryptoData({...cryptoData, address: e.target.value})} />
            
            <button disabled={loading} style={{...btnBase, background: editingId ? colors.accent : colors.primary}} onClick={handleUpdateWallet}>
              {loading ? "PROCESSING..." : editingId ? "UPDATE WALLET" : "SUBMIT FOR USERS"}
            </button>
            {editingId && <button onClick={resetForms} style={{...btnBase, background: 'transparent', border: `1px solid ${colors.border}`, color: '#fff'}}>CANCEL EDIT</button>}
          </>
        )}

        {activeTab === 'bank' && (
          <>
            <input style={inputStyle} placeholder="Bank Name" value={bankData.bankName} onChange={(e) => setBankData({...bankData, bankName: e.target.value})} />
            <input style={inputStyle} placeholder="Holder Name" value={bankData.receiverName} onChange={(e) => setBankData({...bankData, receiverName: e.target.value})} />
            <input style={inputStyle} placeholder="Account No." value={bankData.accountNumber} onChange={(e) => setBankData({...bankData, accountNumber: e.target.value})} />
            <input style={inputStyle} placeholder="IFSC Code" value={bankData.ifsc} onChange={(e) => setBankData({...bankData, ifsc: e.target.value})} />
            <button disabled={loading} style={{...btnBase, background: editingId ? colors.accent : colors.primary}} onClick={handleUpdateBankApp('bank')}>
              {loading ? "PROCESSING..." : editingId ? "UPDATE BANK" : "ADD BANK"}
            </button>
            {editingId && <button onClick={resetForms} style={{...btnBase, background: 'transparent', border: `1px solid ${colors.border}`, color: '#fff'}}>CANCEL EDIT</button>}
          </>
        )}

        {activeTab === 'thirdparty' && (
          <>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <select style={inputStyle} value={thirdPartyData.appName} onChange={(e) => setThirdPartyData({...thirdPartyData, appName: e.target.value})}>
                    {apps.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <button onClick={() => removeListItem('App', thirdPartyData.appName)} style={{ background: 'none', border: 'none', color: colors.accent, cursor: 'pointer', marginBottom: '10px' }}>🗑️</button>
            </div>
            <input style={inputStyle} placeholder="ID / Details" value={thirdPartyData.details} onChange={(e) => setThirdPartyData({...thirdPartyData, details: e.target.value})} />
            <input style={inputStyle} placeholder="Receiver Name" value={thirdPartyData.receiverName} onChange={(e) => setThirdPartyData({...thirdPartyData, receiverName: e.target.value})} />
            <button disabled={loading} style={{...btnBase, background: editingId ? colors.accent : colors.primary}} onClick={handleUpdateBankApp('thirdParty')}>
              {loading ? "PROCESSING..." : editingId ? "UPDATE APP" : "ADD APP"}
            </button>
            {editingId && <button onClick={resetForms} style={{...btnBase, background: 'transparent', border: `1px solid ${colors.border}`, color: '#fff'}}>CANCEL EDIT</button>}
          </>
        )}
      </div>

      {filteredHistory.length > 0 && (
        <div style={{ ...cardStyle, maxWidth: "100%", marginTop: "40px" }}>
          <h3 style={{ color: colors.primary, fontSize: "14px", marginBottom: "20px" }}>{activeTab === 'bank' ? 'IF BANK LEDGER' : 'ACTIVE CONFIGURATION LEDGER'}</h3>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", color: colors.textMuted, fontSize: "11px", borderBottom: `1px solid ${colors.border}` }}>
                {activeTab === 'bank' ? (
                  <><th>BANK NAME</th><th>ACCOUNT NO</th><th>IFSC</th><th>HOLDER</th></>
                ) : (
                  <><th>{activeTab === 'wallet' ? 'BLOCKCHAIN' : 'APP'}</th><th>PRIMARY DATA</th>{activeTab !== 'wallet' && <th>RECEIVER</th>}</>
                )}
                <th style={{ textAlign: 'right' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.map((item) => {
                const data = item[item.type] || {};
                return (
                  <tr key={String(item._id)} style={{ borderBottom: `1px solid rgba(255,255,255,0.05)`, fontSize: "13px" }}>
                    <td style={{ padding: "15px 0" }}>{data.bankName || data.blockchain || data.appName}</td>
                    <td>{data.accountNumber || data.address || data.details}</td>
                    {activeTab === 'bank' ? (
                      <><td>{data.ifsc}</td><td>{data.receiverName}</td></>
                    ) : (
                      <>{activeTab !== 'wallet' && <td>{data.receiverName || "---"}</td>}</>
                    )}
                    <td style={{ textAlign: 'right' }}>
                      <button onClick={() => handleEdit(item)} style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontWeight: 'bold', marginRight: '15px' }}>EDIT</button>
                      <button onClick={() => setShowConfirmDelete(item._id)} style={{ background: 'none', border: 'none', color: colors.accent, cursor: 'pointer', fontWeight: 'bold' }}>DELETE</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FinanceManagement;